/**
 * Pure alert-filter helpers used by `alert-notifications.tsx`.
 *
 * Keeping these as standalone functions (rather than inline in the
 * provider) lets unit tests cover every branch without having to
 * stand up a full React Query + auth + site context.
 */
import type { AlertViewModel } from '../api/adapters';
import type { BatteryAlertPreferences } from './user-preferences';

/** Decide whether an alert should appear in the bell's recent dropdown.
 *  `info`-tier system alerts (e.g. the mock publisher's heartbeat) are
 *  intentionally hidden — they generate noise without giving the
 *  operator anything to act on. Toasts still surface them when they
 *  first arrive (an info-tier alert isn't a toast in the current logic
 *  anyway) but the dropdown keeps its focus on actionable items. */
export function isBellVisible(alert: AlertViewModel): boolean {
  if (alert.category === 'system' && alert.severity === 'info') return false;
  if (alert.source.toLowerCase().includes('heartbeat')) return false;
  return true;
}

/** The server's low-battery rule (`alerts_engine._RULE_ROBOT_BATTERY_LOW`). */
export const BATTERY_LOW_RULE = 'robot_battery_low';

/** Decide whether an alert is toast-worthy: critical or warning. */
export function isToastWorthy(alert: AlertViewModel): boolean {
  return alert.severity === 'critical' || alert.severity === 'warning';
}

/**
 * Whether an alert may POP UP on this computer -- its toast, its chime and
 * its desktop notification -- by this person's Profile choice (Akash,
 * 2026-10-31). Only a LOW `robot_battery_low` row (a warning) is ever held
 * back, and only when "Pop up for low battery" is unticked; a very low one
 * (critical) always pops up, and every other rule is untouched. The bell
 * and the Alerts screen list every row whatever this says: a per-browser
 * choice never hides a server row.
 */
export function popsUp(
  lowPopups: BatteryAlertPreferences['lowPopups'],
  alert: AlertViewModel,
): boolean {
  if (alert.rule !== BATTERY_LOW_RULE || alert.severity === 'critical') return true;
  return lowPopups;
}
